import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Box, Button, IconButton, Typography } from "@mui/material";
import type { TranslateFn } from "../types";
import { toTestId } from "../utils/catalog";

type ProjectResolutionListProps = {
  t: TranslateFn;
  emails: string[];
  addingToProject: Record<string, boolean>;
  resolvedProjectEmails: Record<string, boolean>;
  disabled?: boolean;
  onAddToProject: (email: string) => void;
  onRemoveEmail: (email: string) => void;
};

const ProjectResolutionList = ({
  t,
  emails,
  addingToProject,
  resolvedProjectEmails,
  disabled = false,
  onAddToProject,
  onRemoveEmail,
}: ProjectResolutionListProps) => {
  if (!emails.length) return null;

  return (
    <Box className="mt-3 space-y-2">
      {emails.map((email) => {
        const isAdding = addingToProject[email];
        const isAdded = resolvedProjectEmails[email];

        return (
          <Box className={`flex items-center justify-between gap-2`}>
            <Box
              key={email}
              className={`flex-1 flex items-center justify-between rounded-[8px] p-1  ${
                isAdded ? "bg-[var(--secondary)]" : "bg-[var(--muted)]"
              }`}
            >
              <Box className="flex items-center gap-2">
                {isAdded ? (
                  <CheckIcon
                    fontSize="small"
                    className="text-[var(--foreground)]"
                  />
                ) : (
                  <AddIcon
                    fontSize="small"
                    className="text-[var(--muted-foreground)]"
                  />
                )}
                <Typography className="text-sm text-[var(--foreground)]">
                  {email}
                </Typography>
              </Box>
              <Box className="flex items-center gap-2">
                {isAdded ? (
                  <Typography className="text-sm text-[var(--foreground)] p-2">
                    {t("aiCatalog.shareDialog.added")}
                  </Typography>
                ) : (
                  <Button
                    data-testid={`ai-catalog-share-add-to-project-${toTestId(email)}`}
                    size="small"
                    onClick={() => onAddToProject(email)}
                    disabled={disabled || isAdding}
                    className="min-w-[110px] normal-case text-[var(--foreground)]"
                  >
                    {isAdding
                      ? t("aiCatalog.shareDialog.adding")
                      : t("aiCatalog.shareDialog.addToProject")}
                  </Button>
                )}
              </Box>
            </Box>
            <IconButton
              data-testid={`ai-catalog-share-resolve-email-${toTestId(email)}`}
              size="small"
              onClick={() => onRemoveEmail(email)}
              className="h-11 w-11 bg-[var(--muted)] hover:bg-[var(--secondary)] rounded-[8px]"
            >
              <CloseIcon
                className="text-[var(--secondary-foreground)]"
                fontSize="small"
              />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
};

export default ProjectResolutionList;
